import React, { useRef, useState } from 'react';
import './index.scss';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { ErrorMessage, Field, Formik, FormikProps } from 'formik';
import lodashFirst from 'lodash/first';
import { AppState } from '$/store';
import { CompanyService } from '$/services';
import { importCompanies } from '$/store/company/actions';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';

interface IBulkImportCompanies {
  active: boolean;
  onImportSubmit: () => void;
  onCloseModal: () => void;
  dispatchImportCompanies: (companiesFile: File) => Promise<void>;
}

interface CompanyImportValues {
  companiesFile: File | null;
}

const BulkImportCompanies: React.FC<IBulkImportCompanies> = props => {
  const { active, onImportSubmit, onCloseModal, dispatchImportCompanies } = props;

  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState<string>();

  const formikRef = useRef<FormikProps<CompanyImportValues>>(null);

  const handleCloseModal = () => {
    setImportError(false);
    setImportErrorMessage(undefined);
    setIsImporting(false);

    if (formikRef.current) {
      formikRef.current.resetForm();
    }

    onCloseModal();
  };

  const importCompaniesAsync = async (values: CompanyImportValues) => {
    if (!values.companiesFile) return;

    try {
      setImportError(false);
      setImportErrorMessage(undefined);
      setIsImporting(true);

      await dispatchImportCompanies(values.companiesFile);

      if (formikRef.current) {
        formikRef.current.resetForm();
      }

      onImportSubmit();
    } catch (err: any) {
      setImportError(true);
      setImportErrorMessage(err.response ? err.response.data : 'Error importing data');
    } finally {
      setIsImporting(false);
    }
  };

  const getTemplateFile = async () => {
    await CompanyService.getImportTemplate();
  };

  const getExtension = (fileName: string) => {
    return fileName.substring(fileName.lastIndexOf('.') + 1);
  };

  const schema = Yup.object({
    companiesFile: Yup.mixed()
      .required('File is required')
      .test({
        message: 'Please provide a supported file type (.xlsx)',
        test: (file, context) => {
          const isValid = getExtension((file as File).name) === 'xlsx';
          if (!isValid) context?.createError();
          return isValid;
        },
      }),
  });

  const initialValues: CompanyImportValues = {
    companiesFile: null,
  };

  return (
    <Modal title="Bulk Import Companies" active={active} forForm onClose={handleCloseModal}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values: CompanyImportValues) => {
          await importCompaniesAsync(values);
        }}>
        {({ handleSubmit }) => (
          <form className="import-company-form" onSubmit={handleSubmit}>
            <div className="search_block">
              <Field
                name="companiesFile"
                render={({ form }: { [key: string]: any }) => {
                  const handleFileChange = (ev: React.FormEvent<HTMLInputElement>) => {
                    const file = lodashFirst(ev.currentTarget.files);
                    form.setFieldValue('companiesFile', file);
                  };
                  return (
                    <>
                      <div className="search_block_title">{'Import File'}</div>
                      <input name="companiesFile" type="file" disabled={isImporting} onChange={handleFileChange} />
                      <button type="button" className="template_link" onClick={getTemplateFile}>
                        Download Import Template.xlsx
                      </button>
                    </>
                  );
                }}
              />
            </div>
            <ErrorMessage name="companiesFile">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <div className="btn_container">
              <BixButton useLoading isLoading={isImporting} disabled={isImporting} type="submit">
                Import Companies
              </BixButton>
            </div>
            {!!importError && <p className="error">{importErrorMessage}</p>}
          </form>
        )}
      </Formik>
    </Modal>
  );
};

const mapStateToProps = (_state: AppState) => ({});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchImportCompanies: (companiesFile: File) => dispatch(importCompanies(companiesFile)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkImportCompanies);
